import type {
  GraphSnapshot,
  MaterialNodeSpec,
  OperationNodeSpec,
  FlowEdgeSpec,
  GroupSpec,
} from '../../graph/domain/types';
import type { WorkflowGraph } from '../../graph';
import type { Store } from '../types';
import { withSyncGuard } from '../sync/syncFlag';
import { recomputeDerivation } from '../recomputeDerivation';
import { runWithoutDirtyTracking } from '../dirtyTracking';
import { analyzeGroupSubgraph } from '../../graph/domain/groupTopology';

type SetState = (partial: Partial<Store> | ((state: Store) => Partial<Store>)) => void;
type GetState = () => Store;

/**
 * Validate groups during load-time hydration.
 * Sets invalidReason on any group that is disconnected, has gaps, or has duplicates.
 * Returns validated groups record with invalidReason set where applicable.
 */
function validateGroupsOnLoad(
  groups: Record<string, GroupSpec>,
  materialNodes: Record<string, MaterialNodeSpec>,
  operationNodes: Record<string, OperationNodeSpec>,
  flowEdges: Record<string, FlowEdgeSpec>,
): Record<string, GroupSpec> {
  const validatedGroups: Record<string, GroupSpec> = {};

  const graphData = {
    materialNodes,
    operationNodes,
    flowEdges,
    groups, // Will be populated incrementally
    graphComments: [] as any[], // Add graphComments to satisfy type
  };

  for (const [groupId, group] of Object.entries(groups)) {
    if (!group.stepIds || group.stepIds.length === 0) {
      // Empty group — skip validation
      validatedGroups[groupId] = group;
      continue;
    }

    // Analyze group topology
    const analysis = analyzeGroupSubgraph(group.stepIds, graphData);

    if (analysis.ok) {
      // Group is valid — remove invalidReason if present, normalize order
      validatedGroups[groupId] = {
        ...group,
        stepIds: analysis.ordered,
        invalidReason: undefined,
      };
    } else {
      // Group is invalid — flag with reason
      validatedGroups[groupId] = {
        ...group,
        invalidReason: analysis.reason,
      };
    }
  }

  return validatedGroups;
}

export function createSnapshotActions(
  set: SetState,
  get: GetState,
  getGraph: () => WorkflowGraph | null | undefined,
) {
  return {
    hydrate(snapshot: GraphSnapshot): void {
      const materialNodes: Record<string, MaterialNodeSpec> = {};
      for (const spec of snapshot.materialNodes) {
        if (!spec.id) throw new Error('Snapshot material missing id');
        materialNodes[spec.id] = spec;
      }

      const operationNodes: Record<string, OperationNodeSpec> = {};
      for (const spec of snapshot.operationNodes) {
        if (!spec.id) throw new Error('Snapshot operation missing id');
        operationNodes[spec.id] = spec;
      }

      const flowEdges: Record<string, FlowEdgeSpec> = {};
      for (const spec of snapshot.flowEdges) {
        if (!spec.id) throw new Error('Snapshot edge missing id');
        flowEdges[spec.id] = spec;
      }

      const rawGroups: Record<string, GroupSpec> = {};
      for (const spec of snapshot.groups) {
        if (!spec.id) throw new Error('Snapshot group missing id');
        rawGroups[spec.id] = spec;
      }

      // Validate groups and set invalidReason where applicable
      const groups = validateGroupsOnLoad(rawGroups, materialNodes, operationNodes, flowEdges);

      runWithoutDirtyTracking(() => {
        set((state) => ({
            graphData: { materialNodes, operationNodes, flowEdges, groups, graphComments: state.graphData.graphComments ?? [] },
            selection: { cellIds: [] },
            modals: { stack: [] },
            ui: {
              isDirty: false,
              isReadOnly: false,
              isLoading: false,
              currentUser: state.ui.currentUser ?? null,
              versionId: state.ui.versionId ?? null,
              isProtocolLocked: state.ui.isProtocolLocked ?? false,
              isLifecycleLocked: state.ui.isLifecycleLocked ?? false,
              isArchived: state.ui.isArchived ?? false,
              isArchivalInProgress: state.ui.isArchivalInProgress ?? false,
              runsExist: state.ui.runsExist ?? false,
              lockReason: state.ui.lockReason ?? null,
              canEditContent: state.ui.canEditContent ?? true,
            },
            sync: { syncInProgress: false },
            history: { undoStack: [], redoStack: [] },
          }));
      });

      withSyncGuard(set, () => {
        getGraph()?.hydrateFromSnapshot(snapshot);
      });
    },

    /**
     * Seeds Zustand graphData from a snapshot WITHOUT touching the maxGraph
     * instance. Use this when the graph was already hydrated externally
     * (e.g., via GraphEditorProvider.initialSnapshot) and you only need the
     * store state populated so that useVisualGraphSync can build visual edges.
     */
    seedStoreState(snapshot: GraphSnapshot): void {
      const materialNodes: Record<string, MaterialNodeSpec> = {};
      for (const spec of snapshot.materialNodes) {
        if (!spec.id) throw new Error('Snapshot material missing id');
        materialNodes[spec.id] = spec;
      }
      const operationNodes: Record<string, OperationNodeSpec> = {};
      for (const spec of snapshot.operationNodes) {
        if (!spec.id) throw new Error('Snapshot operation missing id');
        operationNodes[spec.id] = spec;
      }
      const flowEdges: Record<string, FlowEdgeSpec> = {};
      for (const spec of snapshot.flowEdges) {
        if (!spec.id) throw new Error('Snapshot edge missing id');
        flowEdges[spec.id] = spec;
      }
      const rawGroups: Record<string, GroupSpec> = {};
      for (const spec of snapshot.groups) {
        if (!spec.id) throw new Error('Snapshot group missing id');
        rawGroups[spec.id] = spec;
      }

      // Validate groups and set invalidReason where applicable
      const groups = validateGroupsOnLoad(rawGroups, materialNodes, operationNodes, flowEdges);

      runWithoutDirtyTracking(() => {
        set((state) => ({ graphData: { materialNodes, operationNodes, flowEdges, groups, graphComments: state.graphData.graphComments ?? [] } }));
      });
    },

    toSnapshot(): GraphSnapshot {
      // Refresh every material's derivation from current topology so saved data
      // is always consistent, regardless of which action last mutated the graph.
      const graphData = recomputeDerivation(get().graphData);
      return {
        materialNodes: Object.values(graphData.materialNodes),
        operationNodes: Object.values(graphData.operationNodes),
        flowEdges: Object.values(graphData.flowEdges),
        groups: Object.values(graphData.groups),
        schemaVersion: '2.0',
      };
    },

    reset(): void {
      runWithoutDirtyTracking(() => {
        set((state) => ({
          graphData: { materialNodes: {}, operationNodes: {}, flowEdges: {}, groups: {}, graphComments: [] },
          selection: { cellIds: [] },
          modals: { stack: [] },
          ui: {
            isDirty: false,
            isReadOnly: false,
            isLoading: false,
            currentUser: null,
            versionId: null,
            isProtocolLocked: false,
            isLifecycleLocked: false,
            isArchived: false,
            isArchivalInProgress: false,
            runsExist: false,
            lockReason: null,
            canEditContent: true,
          },
          sync: { syncInProgress: false },
          history: { undoStack: [], redoStack: [] },
        }));
      });

      withSyncGuard(set, () => {
        getGraph()?.clear();
      });
    },
  };
}
